import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, DollarSign, Star, ArrowRight, LayoutGrid, List, Users, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LanguageBadge } from '../components/LanguageBadge';
import { isTreatxAdmin } from '../utils/unifiedAuth';
import api from '../utils/api';
import SEO from '../components/SEO';


const TreatmentsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdminViewer = isTreatxAdmin();

  useEffect(() => {
    // P4-4G: use /public/treatments (STEP 4F hygiene-filtered canonical list)
    // instead of /discover/treatments (polluted: placeholders, duplicates, €0).
    api.get(`/public/treatments`)
      .then(({ data }) => data).catch(() => (Promise.reject()))
      .then(data => setTreatments(data.treatments || []))
      .catch(() => setTreatments([]))
      .finally(() => setLoading(false));
  }, [language]);

  const filteredTreatments = activeTab === 'all'
    ? treatments
    : treatments.filter(t => (t.genderGroups || []).includes(activeTab));

  const TreatmentCard = ({ treatment }) => {
    const isPlaceholder = treatment.status === 'coming_soon';
    return (
      <Card className="hover:shadow-lg transition-shadow h-full flex flex-col" data-testid={`treatment-card-${treatment.slug}`}>
        <div className="relative h-48 overflow-hidden rounded-t-lg">
          {/* TREATX-CATALOGUE-IMAGE-GUARD: the canonical catalogue currently
              carries no imagery, so `image` arrives as an empty string. An
              <img src=""> renders as a broken asset; show a neutral tile
              instead. No placeholder photography is invented here. */}
          {treatment.image ? (
            <img
              src={treatment.image}
              alt={treatment.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-100"
                 data-testid={`treatment-image-placeholder-${treatment.slug}`}>
              <Users className="h-7 w-7 text-slate-300" aria-hidden="true" />
            </div>
          )}
          {treatment.featured && (
            <Badge className="absolute top-3 right-3 bg-purple-600">
              <Star className="h-3 w-3 mr-1" />
              Popular
            </Badge>
          )}
          {isPlaceholder && (
            <Badge className="absolute top-3 right-3 bg-gray-500">
              Coming Soon
            </Badge>
          )}
        </div>
        
        <CardHeader>
          <CardTitle className="text-xl">{treatment.name}</CardTitle>
          <CardDescription className="line-clamp-2">
            {treatment.shortDescription}
          </CardDescription>
          {language !== 'en' && (
            <LanguageBadge languageScore={treatment.language_score || 0} showFallbackHint={true} className="mt-1" />
          )}
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col justify-between">
          <div className="space-y-3 mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <DollarSign className="h-4 w-4 mr-2 text-green-600" />
              <span className="font-semibold">
                {treatment.priceRange?.currency || '€'}{(treatment.priceRange?.min || 0).toLocaleString()} - {treatment.priceRange?.currency || '€'}{(treatment.priceRange?.max || 0).toLocaleString()}
              </span>
              {treatment.priceRange?.note && (
                <span className="text-xs text-gray-500 ml-2">({treatment.priceRange.note})</span>
              )}
            </div>
            
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="h-4 w-4 mr-2 text-blue-600" />
              <span>Recovery: {treatment.recoveryTime}</span>
            </div>
            
            <div className="flex flex-wrap gap-1">
              <Badge variant="outline" className="text-xs">{treatment.category}</Badge>
            </div>
          </div>
          
          <Button 
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            onClick={() => navigate(`/treatments/${treatment.slug}`)}
            disabled={isPlaceholder}
            data-testid={`view-treatment-${treatment.slug}`}
          >
            {isPlaceholder ? 'Coming Soon' : 'View Details'}
            {!isPlaceholder && <ArrowRight className="h-4 w-4 ml-2" />}
          </Button>
        </CardContent>
      </Card>
    );
  };

  const TreatmentListCard = ({ treatment }) => {
    const isPlaceholder = treatment.status === 'coming_soon';
    const cur = treatment.priceRange?.currency || '€';
    return (
      <div className="p-4 space-y-3" data-testid={`treatment-mobile-row-${treatment.slug}`}>
        <div className="flex items-start gap-3">
          {treatment.image ? (
            <img src={treatment.image} alt={treatment.name} className="w-16 h-12 rounded-lg object-cover flex-none" />
          ) : (
            <div className="flex h-12 w-16 flex-none items-center justify-center rounded-lg bg-slate-100">
              <Users className="h-4 w-4 text-slate-300" aria-hidden="true" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold leading-tight">{treatment.name}</p>
            <p className="text-sm text-gray-500 line-clamp-2">{treatment.shortDescription}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="text-xs">{treatment.category}</Badge>
          {treatment.genderGroups?.map(g => (
            <Badge key={g} variant="secondary" className="text-xs capitalize">{g}</Badge>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-semibold text-green-600">
            {cur}{(treatment.priceRange?.min || 0).toLocaleString()} - {cur}{(treatment.priceRange?.max || 0).toLocaleString()}
          </span>
          <span className="text-gray-600 flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
            {treatment.recoveryTime}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => navigate(`/treatments/${treatment.slug}`)}
          disabled={isPlaceholder}
          data-testid={`mobile-view-details-${treatment.slug}`}
        >
          {isPlaceholder ? 'Coming Soon' : 'View Details'}
        </Button>
      </div>
    );
  };

  const TreatmentRow = ({ treatment }) => {
    const isPlaceholder = treatment.status === 'coming_soon';
    return (
      <tr className="border-b hover:bg-gray-50 transition-colors" data-testid={`treatment-row-${treatment.slug}`}>
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            {treatment.image ? (
              <img
                src={treatment.image}
                alt={treatment.name}
                className="w-16 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-12 w-16 items-center justify-center rounded-lg bg-slate-100"
                   data-testid={`treatment-row-image-placeholder-${treatment.slug}`}>
                <Users className="h-4 w-4 text-slate-300" aria-hidden="true" />
              </div>
            )}
            <div>
              <p className="font-semibold">{treatment.name}</p>
              <p className="text-sm text-gray-500 line-clamp-1">{treatment.shortDescription}</p>
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          <Badge variant="outline">{treatment.category}</Badge>
        </td>
        <td className="py-4 px-4">
          <div className="flex flex-wrap gap-1">
            {treatment.genderGroups?.slice(0, 2).map(g => (
              <Badge key={g} variant="secondary" className="text-xs capitalize">{g}</Badge>
            ))}
            {treatment.genderGroups?.length > 2 && (
              <Badge variant="secondary" className="text-xs">+{treatment.genderGroups.length - 2}</Badge>
            )}
          </div>
        </td>
        <td className="py-4 px-4">
          <span className="font-semibold text-green-600">
            {treatment.priceRange?.currency || '€'}{(treatment.priceRange?.min || 0).toLocaleString()} - {treatment.priceRange?.currency || '€'}{(treatment.priceRange?.max || 0).toLocaleString()}
          </span>
        </td>
        <td className="py-4 px-4 text-sm text-gray-600">
          {treatment.recoveryTime}
        </td>
        <td className="py-4 px-4">
          <div className="flex gap-2">
            <Button 
              size="sm"
              variant="outline"
              onClick={() => navigate(`/treatments/${treatment.slug}`)}
              disabled={isPlaceholder}
              data-testid={`list-view-details-${treatment.slug}`}
            >
              View Details
            </Button>
            {!isAdminViewer && (
              <Button 
                size="sm"
                className="bg-gradient-to-r from-blue-600 to-purple-600"
                onClick={() => navigate(`/treatments/${treatment.slug}`)}
                disabled={isPlaceholder}
                data-testid={`list-get-quote-${treatment.slug}`}
              >
                Get Offer
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const TreatmentContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      );
    }

    if (viewMode === 'list') {
      return (
        <Card>
          <CardContent className="p-0">
            {/* TREATX-TREATMENTS-MOBILE-01: six columns cannot be read on a
                phone, so the same rows are stacked below `md`. The table —
                unchanged — remains the desktop presentation. No information is
                hidden: every field of the row is present in the stacked card. */}
            <div className="md:hidden divide-y" data-testid="treatments-mobile-list">
              {filteredTreatments.map(treatment => (
                <TreatmentListCard key={treatment.slug} treatment={treatment} />
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Treatment</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Gender</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Price Range</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Recovery</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTreatments.map(treatment => (
                    <TreatmentRow key={treatment.slug} treatment={treatment} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTreatments.map(treatment => (
          <TreatmentCard key={treatment.slug} treatment={treatment} />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <SEO
        title="Medical Treatments Catalog"
        description="Browse TreatX's catalog of verified medical treatments — dental, cosmetic, orthopedic, weight-loss, hair restoration and more — with transparent price ranges and vetted providers worldwide."
        canonicalPath="/treatments"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Medical Treatments Catalog',
          url: 'https://treatx.health/treatments',
        }}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            All Treatments for Your Journey
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Discover world-class medical treatments tailored to your needs. Safe, affordable, and trusted by thousands.
          </p>
        </div>
      </section>

      {/* Treatments Catalog */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="all" className="space-y-8" onValueChange={setActiveTab}>
          {/* Filter Tabs and View Toggle */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <TabsList className="grid grid-cols-4 w-full max-w-2xl">
              <TabsTrigger value="all" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                All
              </TabsTrigger>
              <TabsTrigger value="women" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white">
                Women
              </TabsTrigger>
              <TabsTrigger value="men" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white">
                Men
              </TabsTrigger>
              <TabsTrigger value="non-binary" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                Non-binary
              </TabsTrigger>
            </TabsList>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-white border rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Grid View"
                data-testid="grid-view-toggle"
              >
                <LayoutGrid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="List View"
                data-testid="list-view-toggle"
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-blue-600">{filteredTreatments.length}</span> treatments
              {activeTab !== 'all' && ` for ${activeTab}`}
            </p>
            <p className="text-sm text-gray-500">
              {viewMode === 'grid' ? 'Card View' : 'List View'}
            </p>
          </div>

          {/* Treatment Content */}
          <TabsContent value="all" className="mt-8">
            <TreatmentContent />
          </TabsContent>

          <TabsContent value="women" className="mt-8">
            <TreatmentContent />
          </TabsContent>

          <TabsContent value="men" className="mt-8">
            <TreatmentContent />
          </TabsContent>

          <TabsContent value="non-binary" className="mt-8">
            <TreatmentContent />
          </TabsContent>
        </Tabs>

        {/* No Results */}
        {!loading && filteredTreatments.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No treatments found</h3>
            <p className="text-gray-500">Try selecting a different category</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default TreatmentsPage;
